import { useEffect, useRef, useState, useCallback } from 'react';

export interface IdleTimeoutConfig {
  /** Idle timeout duration in milliseconds (default: 30 minutes) */
  timeoutMs?: number;
  /** Warning time before timeout in milliseconds (default: 2 minutes) */
  warningMs?: number;
  /** Callback when timeout occurs */
  onTimeout: () => void;
  /** Callback when warning should be shown */
  onWarning?: (timeRemaining: number) => void;
  /** Whether the timeout is enabled (typically based on user authentication state) */
  enabled?: boolean;
}

/**
 * Custom hook to manage idle timeout with activity tracking
 * Tracks mouse, keyboard, and API call activity to reset the timer
 */
export function useIdleTimeout({
  timeoutMs = 30 * 60 * 1000, // 30 minutes default
  warningMs = 2 * 60 * 1000, // 2 minutes default
  onTimeout,
  onWarning,
  enabled = true,
}: IdleTimeoutConfig) {
  const [isWarning, setIsWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(timeoutMs);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isPausedRef = useRef<boolean>(false);
  const isWarningRef = useRef<boolean>(false);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  // Reset the idle timer
  const resetTimer = useCallback((forceReset = false) => {
    // If paused and not forcing, don't reset
    if (isPausedRef.current && !forceReset) {
      return;
    }

    // If not enabled, don't reset
    if (!enabled && !forceReset) {
      return;
    }

    // If in warning state, only reset if forced (e.g., from API call or "Stay Logged In" button)
    // This prevents accidental resets from mouse/keyboard during warning dialog
    if (isWarningRef.current && !forceReset) {
      return;
    }

    // Clear pause state if forcing reset
    if (forceReset) {
      isPausedRef.current = false;
    }

    clearTimers();
    lastActivityRef.current = Date.now();
    setIsWarning(false);
    isWarningRef.current = false;
    setTimeRemaining(timeoutMs);

    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      setIsWarning(true);
      isWarningRef.current = true;
      const remaining = timeoutMs - warningMs;
      setTimeRemaining(remaining);
      onWarning?.(remaining);
    }, timeoutMs - warningMs);

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, timeoutMs);
  }, [enabled, timeoutMs, warningMs, onTimeout, onWarning, clearTimers]);

  // Extend session (called when user clicks "Stay Logged In" or makes API call)
  const extendSession = useCallback((forceReset = false) => {
    resetTimer(forceReset);
  }, [resetTimer]);

  // Pause timer (useful when modal is shown)
  const pauseTimer = useCallback(() => {
    isPausedRef.current = true;
    clearTimers();
  }, [clearTimers]);

  // Resume timer (unpause without resetting)
  const resumeTimer = useCallback(() => {
    isPausedRef.current = false;
  }, []);

  // Track user activity
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      // Only reset if not in warning state (to prevent resetting during countdown)
      // Mouse/keyboard activity during warning should not reset (user might click accidentally)
      if (!isWarningRef.current) {
        resetTimer();
      }
    };

    // Mouse events
    const mouseEvents = ['mousedown', 'mousemove', 'click', 'scroll', 'touchstart'];
    mouseEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Keyboard events
    const keyboardEvents = ['keydown', 'keypress'];
    keyboardEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize timer
    resetTimer();

    return () => {
      mouseEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      keyboardEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [enabled, resetTimer, clearTimers]);

  // Sync warning ref with state
  useEffect(() => {
    isWarningRef.current = isWarning;
  }, [isWarning]);

  // Update countdown when in warning state
  useEffect(() => {
    if (!isWarning || !enabled || isPausedRef.current) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, timeoutMs - elapsed);
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        onTimeout();
      }
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [isWarning, enabled, timeoutMs, onTimeout]);

  return {
    isWarning,
    timeRemaining,
    extendSession,
    pauseTimer,
    resumeTimer,
    resetTimer,
  };
}
